import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { Row, Table } from "@tanstack/react-table";
import { Issue, IssueAttributes, IssueFieldId } from "@adsk/acc-issues-types";
import { useUpdateIssue } from "../../../../hooks/useUpdateIssue";
import { useGetIsFieldRequiredByIssueTypeId } from "../../../../utils/hooks/requiredFieldsByTypeHooks";
import { UpdateOrigin } from "../../../../utils/analytics/issueActionsTracking";

export interface EditableCellRendererProps {
  row: Row<Issue>;
  table: Table<Issue>;
}

export type IssueTableMeta = {
  container?: React.RefObject<HTMLDivElement | null>;
};

export const isFieldEditable = (
  issue: Issue,
  fieldKey: IssueFieldId
): boolean => issue.permittedAttributes?.includes(fieldKey) ?? false;

export type InlineFieldChangeFn = (
  issue: Issue,
  changes: Partial<IssueAttributes>,
  handlers?: { onSuccess?: () => void; onFailure?: () => void }
) => void;

export const useIsFieldRequired = (
  issue: Issue,
  fieldId?: IssueFieldId
): boolean => {
  const getIsFieldRequiredByIssueTypeId = useGetIsFieldRequiredByIssueTypeId();

  return useMemo(() => {
    if (!fieldId || !issue.issueTypeId) return false;

    return getIsFieldRequiredByIssueTypeId({
      issueFieldId: fieldId,
      issueTypeId: issue.issueTypeId,
    });
  }, [getIsFieldRequiredByIssueTypeId, issue.issueTypeId, fieldId]);
};

const EditableCellContainer = styled.div`
  min-width: 0;
  flex: 1;
`;

export const useIssueFieldInlineChange = (): InlineFieldChangeFn => {
  const updateIssue = useUpdateIssue();

  return useCallback(
    (issue, changes, handlers) => {
      void updateIssue({
        issueId: issue.id,
        issueChanges: changes,
        analyticsData: {
          origin: UpdateOrigin.INLINE_TABLE,
        },
        onSuccess: handlers?.onSuccess,
        onFailure: handlers?.onFailure,
      });
    },
    [updateIssue]
  );
};

export const InlineFieldTableCell = React.memo(
  ({ testId, children }: { testId: string; children: React.ReactNode }) => {
    return (
      <EditableCellContainer data-testid={testId}>
        {children}
      </EditableCellContainer>
    );
  }
);

InlineFieldTableCell.displayName = "InlineFieldTableCell";
